import React from 'react'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'

// OOBT Hooks
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'

// Custom Hooks
import useMultiSite from '../../hooks/use-multi-site'

// Utils
import {
    buildBreadcrumbLDJSON,
    getHomepageStructuredData,
    buildProductLDJSON,
    buildPDPBreadcrumbs,
    buildStoresBreadcrumbs,
    buildContentBreadcrumbs
} from '../../util/structuredData'

/**
 * SEOStructuredData is a React functional component that generates and injects
 * structured data (in JSON-LD format) into the HTML document head for SEO purposes.
 *
 * This component utilizes the Helmet library to manage changes to the document head,
 * specifically for adding structured data scripts that help search engines understand
 * the content of the page better.
 *
 * The component currently supports the generation of breadcrumb structured data,
 * with the potential to support additional types in the future.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.type - The type of structured data to generate. Currently supports 'breadcrumb'.
 * @param {Object} props.data - The data required to build the structured data. For breadcrumbs, this includes categories.
 *
 * @returns {JSX.Element} A Helmet component containing a script tag with the structured data in JSON-LD format,
 * or an empty fragment if the required data is not provided.
 */
const SEOStructuredData = (props) => {
    const {buildUrl, site} = useMultiSite()
    const appOrigin = useAppOrigin()

    let ldJSONData = {}

    // Build structured data based on the provided type.
    switch (props.type) {
        case 'breadcrumb':
            // Security to avoid issues when data is not passed
            if (!props?.data?.categories) {
                return <></>
            }
            ldJSONData = buildBreadcrumbLDJSON(props.data.categories, buildUrl)
            break
        case 'product':
            // Security to avoid issues when data is not passed
            if (!props?.data?.product) {
                return <></>
            }
            ldJSONData = buildProductLDJSON(
                props?.data?.product,
                site?.id,
                buildUrl,
                appOrigin,
                props?.data?.productID
            )
            break
        case 'productBreadcrumb':
            // Security to avoid issues when data is not passed
            if (!props?.data?.product) {
                return <></>
            }
            ldJSONData = buildPDPBreadcrumbs(
                props?.data?.product,
                props?.data?.product?.c_structuredDataCategories,
                buildUrl
            )
            break
        case 'stores':
            ldJSONData = buildStoresBreadcrumbs(buildUrl)
            break
        case 'content':
            ldJSONData = buildContentBreadcrumbs(
                props?.data?.currentPage,
                props?.data?.homePage,
                props?.data?.homeUrl,
                buildUrl,
                props?.data?.blogCategoryPage
            )
            break
        case 'home':
            ldJSONData = getHomepageStructuredData(props?.data)
            break
        // Other structured data types to be added later
    }

    // Security to ensure we are loading structured data only when data are available
    if (!props.type) {
        return <></>
    }

    return (
        <Helmet>
            <script type="application/ld+json">{JSON.stringify(ldJSONData)}</script>
        </Helmet>
    )
}

SEOStructuredData.displayName = 'SEOStructuredData'

SEOStructuredData.propTypes = {
    data: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    type: PropTypes.string
}

export default SEOStructuredData
